import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import verqoLogo from "@/assets/verqo-logo.png.asset.json";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Logg inn | VERQO" },
      { name: "description", content: "Logg inn på VERQO." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) return;
      const { data: rows } = await supabase
        .from("company_members")
        .select("company_id")
        .eq("user_id", data.session.user.id)
        .order("created_at", { ascending: true })
        .limit(1);
      const companyId = rows?.[0]?.company_id;
      navigate({ to: companyId ? "/dashboard" : "/onboarding", replace: true });
    });
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
        });
        if (error) throw error;
        if (data.session) {
          toast.success("Konto opprettet! La oss sette opp bedriften din.");
          navigate({ to: "/onboarding", replace: true });
        } else {
          toast.success("Sjekk e-posten din for å bekrefte kontoen.");
          setMode("signin");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        const { data: sess } = await supabase.auth.getSession();
        const uid = sess.session?.user.id;
        const { data: rows } = uid
          ? await supabase
              .from("company_members")
              .select("company_id")
              .eq("user_id", uid)
              .order("created_at", { ascending: true })
              .limit(1)
          : { data: null };
        const companyId = rows?.[0]?.company_id;
        toast.success("Velkommen tilbake!");
        navigate({ to: companyId ? "/dashboard" : "/onboarding", replace: true });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Ukjent feil";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen" style={{ background: "#0D0D0F" }}>
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-10">
        <Link to="/" className="mx-auto mb-8 flex flex-col items-center gap-3">
          <img src={verqoLogo.url} alt="VERQO" className="h-24 w-24 rounded-xl object-contain" />
          <span className="text-xs text-white/50">Hvordan kan VERQO hjelpe deg i dag?</span>
        </Link>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur sm:p-8">
          <h1 className="text-xl font-semibold text-white">
            {mode === "signin" ? "Logg inn" : "Opprett konto"}
          </h1>
          <p className="mt-1 text-sm text-white/50">
            {mode === "signin"
              ? "Fortsett med din e-post og passord."
              : "Kom i gang på under ett minutt."}
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">

            <div>
              <label className="mb-1.5 block text-xs font-medium text-white/60">E-post</label>
              <input
                type="email"
                className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none"
                placeholder="deg@firma.no"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-white/60">Passord</label>
              <input
                type="password"
                className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none"
                placeholder="Minst 6 tegn"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
              />
            </div>

            <button type="submit" className="btn btn-primary w-full py-3" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "signin" ? "Logg inn" : "Opprett konto"}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-white/50">
            {mode === "signin" ? (
              <>Har du ikke konto?{" "}
                <button className="font-medium text-primary hover:underline" onClick={() => setMode("signup")}>
                  Opprett en her
                </button>
              </>
            ) : (
              <>Har du allerede konto?{" "}
                <button className="font-medium text-primary hover:underline" onClick={() => setMode("signin")}>
                  Logg inn
                </button>
              </>
            )}
          </div>
        </div>

        <Link to="/" className="mt-6 text-center text-sm text-white/40 hover:text-white">
          ← Tilbake
        </Link>
      </div>
    </div>
  );
}
